import { RouterOutputs } from '@crocodille/api';
import {
  assert,
  Cell,
  identity,
  isNone,
  isSome,
  Maybe,
  removeDiacritics,
  useCellValue,
} from '@crocodille/util';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR, { unstable_serialize } from 'swr';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

import { App } from '../app';
import { useApp } from '../app-provider';
import { useDebounce } from '../hooks/use-debounce';
import { laggy, useMappedSWRResponse } from '../util/swr';
import { useAuthorizedCustomer } from './auth.v2';
import {
  getCartOrderIdentifier,
  useCart,
  useCartOrderIdentifier,
} from './cart';
import { v2_ProductDetail } from '../../public-api';

type DeliveryMethodType = NonNullable<
  RouterOutputs['order']['byTokenValue']
>['deliveryMethodType'];

type Pricing =
  RouterOutputs['product']['all'][number]['products'][number]['variants'][number]['price'];

/**
 * When no order is in card, we use this delivery method type to determine the
 * price.
 *
 * TODO: Determine differently when used for BB.
 */
const FALLBACK_DELIVERY_METHOD_FOR_PRICING =
  'atBranch' satisfies DeliveryMethodType;

/**
 * Determines the price based on the delivery method type.
 */
function getPricingByDeliveryMethodType(
  pricing: Pricing | null,
  deliveryMethodType:
    | DeliveryMethodType
    | undefined = FALLBACK_DELIVERY_METHOD_FOR_PRICING,
): number | null {
  return pricing && deliveryMethodType ? pricing[deliveryMethodType] : null;
}

/**
 * All additional transformations of the product list required on the client.
 */
function postProcessProductList(
  deliveryMethodType: DeliveryMethodType | undefined,
  favoritesSet: Set<string> | null,
) {
  return (taxons: RouterOutputs['product']['all']) =>
    taxons.map(({ products, ...rest }) => ({
      ...rest,
      products: products.map(({ variants, ...rest }) => ({
        ...rest,
        image: (rest.image ?? null) as string | string[] | null,
        favorite: favoritesSet ? favoritesSet.has(rest.code) : null,
        variants: variants.map(({ price, ...rest }) => ({
          ...rest,
          price: getPricingByDeliveryMethodType(price, deliveryMethodType),
        })),
      })),
    }));
}

/**
 * All additional transformations of the product list required on the client.
 */
function postProcessConfiguratorProducts(
  deliveryMethodType: DeliveryMethodType | undefined,
) {
  return (products: RouterOutputs['product']['allConfiguratorProducts']) =>
    products.map(({ configuration, variants, ...rest }) => ({
      ...rest,
      variants: variants.map(({ price, ...rest }) => ({
        ...rest,
        price: getPricingByDeliveryMethodType(price, deliveryMethodType),
      })),
      configuration: configuration.map((property) => ({
        ...property,
        options: property.options.map(({ unitPrice, ...option }) => ({
          unitPrice: getPricingByDeliveryMethodType(
            unitPrice,
            deliveryMethodType,
          ),
          ...option,
        })),
      })),
    }));
}

function postProcessGiftCardProducts(
  deliveryMethodType: DeliveryMethodType | undefined,
) {
  return (groups: RouterOutputs['product']['allGiftCards']) =>
    groups.map(({ products, ...group }) => ({
      ...group,
      products: products.map(({ variants, ...rest }) => ({
        ...rest,
        variants: variants.map(({ price, ...rest }) => ({
          ...rest,
          price: getPricingByDeliveryMethodType(price, deliveryMethodType),
        })),
      })),
    }));
}

/**
 * Represents data of the product list.
 */
export type ProductList = ReturnType<ReturnType<typeof postProcessProductList>>;

/**
 * Represents a single product in the product list.
 */
export type ListProduct = ProductList[number]['products'][number];

/**
 * Represents data of the configurator products.
 */
export type ConfiguratorProducts = ReturnType<
  ReturnType<typeof postProcessConfiguratorProducts>
>;

/**
 * All additional transformations of the product detail required on the client.
 */
function postProcessProductDetail(
  deliveryMethodType: DeliveryMethodType | undefined,
  favoritesSet: Set<string> | null,
) {
  return (product: RouterOutputs['product']['bySlug']) => {
    if (isNone(product)) {
      return product;
    }

    return {
      ...product,
      configuration: product.configuration?.map(({ options, ...rest }) => ({
        ...rest,
        options: options.map(({ unitPrice, ...rest }) => ({
          ...rest,
          unitPrice: getPricingByDeliveryMethodType(
            unitPrice,
            deliveryMethodType,
          ),
        })),
      })),
      favorite: favoritesSet ? favoritesSet.has(product.code) : null,
    };
  };
}

function filterBySearchQuery(searchQuery?: string) {
  const MIN_SEARCH_QUERY_LENGTH = 2;

  if (!searchQuery || searchQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    return identity;
  }

  // Create search regex from search query
  const searchRegex = new RegExp(removeDiacritics(searchQuery), 'i');

  return (taxons: ProductList) => {
    const filteredTaxons = taxons.map(({ products, ...rest }) => ({
      ...rest,
      products: products.filter(
        ({ title, slug, shortDescription, allergens, variants }) => {
          const matchedInStrings = [
            title,
            slug,
            shortDescription,
            allergens,
            ...variants.map((variant) => variant.name),
          ].some((value) => value && searchRegex.test(removeDiacritics(value)));

          return matchedInStrings;
        },
      ),
    }));

    return filteredTaxons.filter(({ products }) => products.length > 0);
  };
}

function filterByTagsAndFavorite(tagFilter: readonly string[] = []) {
  if (!tagFilter || tagFilter.length === 0) {
    return identity;
  }

  return (taxons: ProductList) => {
    const filteredTaxons = taxons.map(({ products, ...rest }) => ({
      ...rest,
      products: products.filter(({ tags, favorite }) => {
        const hasTags = tagFilter.some((filterTag) =>
          tags.some((productTag) => productTag.id === filterTag),
        );

        return hasTags || (tagFilter.includes('favorite') && favorite);
      }),
    }));

    return filteredTaxons.filter(({ products }) => products.length > 0);
  };
}

/**
 * Represents data of the product detail.
 */
export type ProductDetail = NonNullable<
  ReturnType<ReturnType<typeof postProcessProductDetail>>
>;

export interface UseProductListOptions {
  /**
   * Search query.
   */
  readonly searchQuery?: string;
  /**
   * Tag filter.
   */
  readonly tagFilter?: readonly string[];
  /**
   * Branch code fallback for use with SSR.
   */
  readonly branchCode?: Maybe<string>;
  /**
   * When tag filter needs to be changed by hook.
   */
  setTagFilter?(tags: readonly string[]): void;
}

function getProductsKey(params: {
  readonly locale: string;
  readonly branchCode: Maybe<string>;
}) {
  return [
    'product.all',
    {
      locale: params.locale,
      branchCode: params.branchCode ?? undefined,
    },
  ] as const;
}

function getFavoriteProductsKey(customerId: Maybe<number>) {
  return ['product.favoriteProductCodes', customerId ?? null] as const;
}

function useFavoriteProductCodes() {
  const { trpc } = useApp();
  const { data: customer } = useAuthorizedCustomer();

  const response = useSWR(
    getFavoriteProductsKey(customer?.id),
    async () => {
      const codes = await trpc.product.favoriteProductCodes.query();

      return codes;
    },
    {
      use: [laggy],
      revalidateOnMount: true,
      revalidateOnFocus: true,
    },
  );

  const changeFavoriteProduct = useCallback(
    async (productCode: string, favorite: boolean) => {
      await response.mutate(async (favoriteCodes) => {
        if (!favoriteCodes) {
          return favoriteCodes;
        }

        const newState = await trpc.product.changeFavorite.mutate({
          productCode,
          favorite,
        });

        if (newState) {
          return [...favoriteCodes, productCode];
        } else {
          return favoriteCodes.filter((code) => code !== productCode);
        }
      });
    },
    [trpc.product.changeFavorite],
  );

  return { ...response, changeFavoriteProduct };
}

export function useFavoriteProduct(productCode: Maybe<string> | false) {
  const { mutate, isLoading, isValidating, error } = useFavoriteProductCodes();
  const { trpc } = useApp();

  const changeFavoriteProduct = useCallback(
    async (favorite: boolean) => {
      await mutate(async (favoriteCodes) => {
        if (!favoriteCodes || !productCode) {
          return favoriteCodes;
        }

        const newState = await trpc.product.changeFavorite.mutate({
          productCode,
          favorite,
        });

        if (newState) {
          return [...favoriteCodes, productCode];
        } else {
          return favoriteCodes.filter((code) => code !== productCode);
        }
      });
    },
    [productCode, mutate, trpc],
  );

  return { trigger: changeFavoriteProduct, isLoading, isValidating, error };
}

/**
 * Fetches the product list.
 *
 * Use this to display the menu screen.
 *
 * Products here and on details have different structure, so do not interchange them.
 */
export function useProductList({
  searchQuery: searchQuerySource,
  branchCode: branchCodeFallback,
  tagFilter,
  setTagFilter,
}: UseProductListOptions = {}) {
  const { trpc, locale } = useApp();
  const [cartOrderIdentifier] = useCartOrderIdentifier();

  const FAVORITE_TAG = {
    id: 'favorite',
    name: locale === 'en' ? '❤️ Favorite' : '❤️ Oblíbené',
    shortName: locale === 'en' ? '❤️' : '❤️',
    color: 'red' as const,
  };

  const { data: cart } = useCart();
  const deliveryMethodType = cart?.deliveryMethodType;

  const searchQuery = useDebounce(searchQuerySource, 150);

  const productsResponse = useSWR(
    getProductsKey({
      locale,
      branchCode: cartOrderIdentifier?.branchCode ?? branchCodeFallback,
    }),
    async ([_, { branchCode, locale }]) => {
      const products = await trpc.product.all.query({
        locale,
        branchCode,
      });

      return products;
    },
    {
      use: [laggy],
    },
  );

  const { data: favoriteProductCodes } = useFavoriteProductCodes();

  const postProcess = useMemo(
    () =>
      postProcessProductList(
        deliveryMethodType,
        favoriteProductCodes ? new Set(favoriteProductCodes) : null,
      ),
    [deliveryMethodType, favoriteProductCodes],
  );

  const postProcessedResponse = useMappedSWRResponse(
    productsResponse,
    (taxons) => postProcess(taxons),
    [postProcess],
  );

  const tags = useMemo(() => {
    if (!postProcessedResponse.data) {
      return [];
    }

    type Tag =
      RouterOutputs['product']['all'][number]['products'][number]['tags'][number];

    const tags = new Map<string, Tag>();

    for (const taxon of postProcessedResponse.data) {
      for (const product of taxon.products) {
        for (const tag of product.tags) {
          tags.set(tag.id, tag);
        }

        if (product.favorite) {
          tags.set(FAVORITE_TAG.id, FAVORITE_TAG);
        }
      }
    }

    return Array.from(tags.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [postProcessedResponse.data]);

  const searchByQuery = useMemo(
    () => filterBySearchQuery(searchQuery),
    [searchQuery],
  );

  const searchByTagsAndFavorite = useMemo(
    () => filterByTagsAndFavorite(tagFilter),
    [tagFilter],
  );

  const searchedResponse = useMappedSWRResponse(
    postProcessedResponse,
    (taxons) => searchByQuery(searchByTagsAndFavorite(taxons)),
    [searchByQuery, searchByTagsAndFavorite],
  );

  const favoriteProductCount = searchedResponse.data?.reduce(
    (acc, taxon) =>
      acc + taxon.products.filter((product) => product.favorite).length,
    0,
  );

  useEffect(() => {
    if (favoriteProductCount === 0 && tagFilter?.includes?.(FAVORITE_TAG.id)) {
      setTagFilter?.(tagFilter?.filter?.((tag) => tag !== FAVORITE_TAG.id));
    }
  }, [favoriteProductCount, tagFilter, setTagFilter]);

  return {
    ...searchedResponse,
    tags,
  };
}

export async function fetchProductList(
  app: App,
  {
    branchCode,
    tag,
  }: { readonly branchCode: Maybe<string>; tag?: Maybe<string> },
) {
  branchCode =
    branchCode ?? getCartOrderIdentifier(app)?.branchCode ?? branchCode;

  const data = await app.trpc.product.all.query({
    locale: app.locale,
    branchCode,
    tag,
  });

  return data;
}

export function asProductListFallback(
  app: App,
  { branchCode }: { readonly branchCode: Maybe<string> },
  data: Awaited<ReturnType<typeof fetchProductList>>,
) {
  branchCode =
    branchCode ?? getCartOrderIdentifier(app)?.branchCode ?? branchCode;
  const key = unstable_serialize(
    getProductsKey({
      locale: app.locale,
      branchCode,
    }),
  );

  return {
    [key]: data,
  };
}

/**
 * Fetch the product list fallback.
 */
export async function fetchProductListFallback(
  app: App,
  { branchCode }: { readonly branchCode: Maybe<string> },
) {
  const data = await fetchProductList(app, { branchCode });

  return asProductListFallback(app, { branchCode }, data);
}

export type ProductConfigurationPropertyValues = Partial<{
  readonly [propertyId: string]:
    | string // single choice
    | null // single choice, not selected
    | Partial<{ readonly [optionId: string]: number }>; // multiple choice (quantity per option)
}>;

export function isSingleChoiceProperty(
  property: ProductDetail['configuration'][number],
) {
  return property.maxSelectedOptions === 1;
}

export function isFreeformProperty(
  property: ProductDetail['configuration'][number],
) {
  return property.role === 'freeform';
}

export interface ProductDetailState {
  readonly product: ProductDetail | null;
  readonly configurationValues: ProductConfigurationPropertyValues;
  readonly configuration: ProductDetail['configuration'];
  readonly maxSelectedOptions: number | null;
  initialize: (
    product: ProductDetail,
    preferredVariant: Maybe<string>,
    initialConfigurationValues: Maybe<ProductConfigurationPropertyValues>,
  ) => void;
  setSingleChoiceProperty: (
    propertyId: string,
    optionId: string | null,
  ) => void;
  setMultipleChoiceProperty: (
    propertyId: string,
    option: string,
    quantity: number,
  ) => void;
  setFreeformProperty: (propertyId: string, value: string | null) => void;
  clearConfiguration: () => void;
}

export function selectFreeformConfigurationPropertyValue(
  configurationValues: ProductConfigurationPropertyValues,
  propertyId: string,
): string | null {
  const value = configurationValues[propertyId] ?? null;

  if (value === null || typeof value === 'string') {
    return value;
  }

  return null;
}

export function selectSingleChoiceConfigurationPropertyValue(
  configurationValues: ProductConfigurationPropertyValues,
  propertyId: string,
): string | null {
  const value = configurationValues[propertyId] ?? null;

  if (value === null || typeof value === 'string') {
    return value;
  }

  return null;
}

export function selectMultipleChoiceConfigurationPropertyValue(
  configurationValues: ProductConfigurationPropertyValues,
  propertyId: string,
): Partial<{ readonly [optionId: string]: number }> {
  const value = configurationValues[propertyId] ?? {};

  if (typeof value === 'object') {
    return value;
  }

  return {};
}

function evaluateOptionCondition(
  option: ProductDetail['configuration'][number]['options'][number],
  propertyId: string,
  propertyValue: string | null,
) {
  if (!option.when) {
    return true;
  }

  const relevantConditions = option.when.filter(
    (when) => when.property === propertyId,
  );

  if (relevantConditions.length === 0) {
    return true;
  }

  return (
    relevantConditions.some((when) => when.value === propertyValue) ?? false
  );
}

const findDefaultOption = (
  property: ProductDetail['configuration'][number],
  currentValues: ProductDetailState['configurationValues'],
) => {
  const setProperties = Object.keys(currentValues);

  return (
    property.options.find(
      (option) =>
        setProperties
          .filter((p) => p !== property.id)
          .every((p) => {
            const value = currentValues[p];

            if (typeof value === 'string' || value === null) {
              return evaluateOptionCondition(option, p, value);
            } else {
              return true;
            }
          }) && option.default,
    ) ?? null
  );
};

const setSingleChoicePropertyImpl =
  (property: ProductDetail['configuration'][number], optionId: string | null) =>
  (state: ProductDetailState) => {
    if (!property.options.some((option) => option.itemCode === optionId)) {
      return state;
    }

    const propertiesToReevaluate = state.configuration.filter((cfgProperty) =>
      cfgProperty.options.some(
        (option) =>
          option.when?.some?.((when) => when.property === property.id) ?? false,
      ),
    );

    const configurationValues = {
      ...state.configurationValues,
      [property.id]: optionId,
    };

    state = {
      ...state,
      configurationValues,
    };

    for (const reevaluatedProperty of propertiesToReevaluate) {
      const selectedOptionValue = configurationValues[reevaluatedProperty.id];

      const selectedOption = reevaluatedProperty.options.find(
        (option) => option.itemCode === selectedOptionValue,
      );

      if (
        !selectedOption ||
        (selectedOption.when &&
          !evaluateOptionCondition(selectedOption, property.id, optionId))
      ) {
        const defaultOption =
          findDefaultOption(reevaluatedProperty, state.configurationValues)
            ?.itemCode ?? null;

        state = setSingleChoicePropertyImpl(
          reevaluatedProperty,
          defaultOption,
        )(state);
      }
    }

    return state;
  };

const setMultipleChoicePropertyImpl =
  (
    property: ProductDetail['configuration'][number],
    option: string,
    quantity: number,
  ) =>
  (state: ProductDetailState) => {
    if (!property.options.some((o) => o.itemCode === option)) {
      return state;
    }

    let propertyValues = state.configurationValues[property.id];

    assert(propertyValues !== null);
    assert(typeof propertyValues === 'object');

    const selectedPropertyQuantityWithoutOption = Object.entries(
      propertyValues,
    ).reduce((acc, [optionId, quantity]) => {
      if (optionId === option) {
        return acc ?? 0;
      } else {
        return (acc ?? 0) + (quantity ?? 0);
      }
    }, 0);

    const selectedTotalQuantityWithoutOption =
      state.configuration.reduce((acc, p) => {
        if (
          p.id === property.id ||
          (p.role !== 'ingredient' && p.role !== 'half')
        ) {
          return acc;
        }

        const pv = state.configurationValues[p.id] ?? {};

        const total = Object.values(pv).reduce((acc, quantity) => {
          return (acc ?? 0) + (quantity ?? 0);
        }, 0);

        return acc + total;
      }, 0) + selectedPropertyQuantityWithoutOption;

    let configurationValues = state.configurationValues;

    const wrapsPropertyMaximum =
      property.maxSelectedOptions &&
      selectedPropertyQuantityWithoutOption + quantity >
        property.maxSelectedOptions;
    const wrapsProductMaximum =
      state.maxSelectedOptions &&
      selectedTotalQuantityWithoutOption + quantity > state.maxSelectedOptions;

    if (
      property.wrapSelectedOptions &&
      (wrapsProductMaximum || wrapsPropertyMaximum)
    ) {
      propertyValues = {};
      quantity = 1;

      for (const otherProperty of state.configuration) {
        if (
          otherProperty.id === property.id ||
          !otherProperty.wrapSelectedOptions
        ) {
          continue;
        }

        configurationValues = {
          ...configurationValues,
          [otherProperty.id]: {},
        };
      }
    }

    return {
      ...state,
      configurationValues: {
        ...configurationValues,
        [property.id]: {
          ...propertyValues,
          [option]: quantity,
        },
      },
    };
  };

const setFreeformPropertyImpl =
  (property: ProductDetail['configuration'][number], value: string | null) =>
  (state: ProductDetailState) => {
    if (property.role !== 'freeform') {
      return state;
    }

    if (
      property.hint === 'freeform-select' &&
      value != null &&
      !property.options.some((o) => o.itemCode === value)
    ) {
      return state;
    }

    return {
      ...state,
      configurationValues: {
        ...state.configurationValues,
        [property.id]: value,
      },
    };
  };

const useProductDetailState = create<ProductDetailState>((set, get) => ({
  product: null,
  configurationValues: {},
  configuration: [],
  maxSelectedOptions: null,
  initialize: (
    product: ProductDetail,
    preferredVariant: Maybe<string>,
    initialConfigurationValues: Maybe<ProductConfigurationPropertyValues>,
  ) => {
    const variantProperty = product.configuration.find(
      (property) => property.role === 'variant',
    );

    set((state) => {
      const configurationValues = Object.fromEntries(
        product.configuration.map(
          (property) =>
            [
              property.id,
              isSingleChoiceProperty(property) || isFreeformProperty(property)
                ? null
                : {},
            ] as const,
        ),
      );

      if ('isGift' in configurationValues) {
        configurationValues['isGift'] = 'true';
      }

      state = {
        ...state,
        product,
        configuration: product.configuration,
        configurationValues,
        maxSelectedOptions: product.maxSelectedConfigurationOptions ?? null,
      };

      if (variantProperty) {
        initialConfigurationValues = {
          ...initialConfigurationValues,
          [variantProperty.id]:
            preferredVariant ??
            initialConfigurationValues?.[variantProperty.id],
        };
      }

      for (const prop of product.configuration) {
        const defaultOption = findDefaultOption(
          prop,
          state.configurationValues,
        );

        if (isSingleChoiceProperty(prop) && defaultOption) {
          state = setSingleChoicePropertyImpl(
            prop,
            defaultOption.itemCode,
          )(state);
        }

        if (isFreeformProperty(prop) && defaultOption) {
          state = setFreeformPropertyImpl(prop, defaultOption.itemCode)(state);
        }

        if (isFreeformProperty(prop) && initialConfigurationValues?.[prop.id]) {
          state = setFreeformPropertyImpl(
            prop,
            initialConfigurationValues[prop.id] as string,
          )(state);
        }

        if (
          isSingleChoiceProperty(prop) &&
          initialConfigurationValues?.[prop.id]
        ) {
          state = setSingleChoicePropertyImpl(
            prop,
            initialConfigurationValues[prop.id] as string,
          )(state);
        }

        if (
          !isSingleChoiceProperty(prop) &&
          initialConfigurationValues?.[prop.id]
        ) {
          for (const [option, quantity] of Object.entries(
            initialConfigurationValues?.[prop.id] as Record<string, number>,
          )) {
            state = setMultipleChoicePropertyImpl(
              prop,
              option,
              quantity,
            )(state);
          }
        }
      }

      return state;
    });
  },
  setSingleChoiceProperty: (propertyId: string, optionId: string | null) =>
    set((state) => {
      const property = state.configuration.find((p) => p.id === propertyId);

      if (property) {
        return setSingleChoicePropertyImpl(property, optionId)(state);
      } else {
        return state;
      }
    }),
  setMultipleChoiceProperty: (
    propertyId: string,
    option: string,
    quantity: number,
  ) =>
    set((state) => {
      const property = state.configuration.find((p) => p.id === propertyId);

      if (property) {
        return setMultipleChoicePropertyImpl(property, option, quantity)(state);
      } else {
        return state;
      }
    }),
  setFreeformProperty: (propertyId: string, value: string | null) =>
    set((state) => {
      const property = state.configuration.find((p) => p.id === propertyId);

      if (property) {
        return setFreeformPropertyImpl(property, value)(state);
      } else {
        return state;
      }
    }),
  clearConfiguration: () => {
    const { product } = get();

    if (!product) return;

    get().initialize(product, null, null);
  },
}));

function getProductKey(params: {
  readonly branchCode: Maybe<string>;
  readonly slug: string;
  readonly locale: string;
}) {
  return [
    'product.bySlug',
    {
      branchCode: params.branchCode ?? undefined,
      slug: params.slug,
      locale: params.locale,
    },
  ] as const;
}

function convertValuesForProduct(
  configurationValues: ProductConfigurationPropertyValues,
  configuratorProducts: ConfiguratorProducts,
  product: ProductDetail,
) {
  if (configuratorProducts.length === 0) {
    return configurationValues;
  }

  const validSuffixes = configuratorProducts.map((p) => p.code);

  const modifiedValues: {
    [propertyId: string]: string | { [optionId: string]: number } | null;
  } = {};

  for (const [propertyKey, value] of Object.entries(configurationValues)) {
    const property = product.configuration.find((p) => p.id === propertyKey);

    if (!property) {
      continue;
    }

    if (typeof value === 'string') {
      const usedSuffix = validSuffixes.find((suffix) => value.endsWith(suffix));

      if (usedSuffix) {
        modifiedValues[propertyKey] = value.replace(usedSuffix, product.code);
      }
    } else if (isSome(value)) {
      const modifiedValue: { [optionId: string]: number } = {};

      for (const [optionKey, quantity] of Object.entries(value)) {
        const usedSuffix = validSuffixes.find((suffix) =>
          optionKey.endsWith(suffix),
        );

        if (isSome(usedSuffix) && typeof quantity === 'number') {
          modifiedValue[optionKey.replace(usedSuffix, product.code)] = quantity;
        }
      }

      modifiedValues[propertyKey] = modifiedValue;
    }
  }

  return modifiedValues;
}

export function useProductDetailData(
  slug: Maybe<string> | false,
  branchCodeFallback?: string,
  configurationValues?: ProductConfigurationPropertyValues,
) {
  const { trpc, locale } = useApp();
  const [cartOrderIdentifier] = useCartOrderIdentifier();

  const { data: cart } = useCart();
  const deliveryMethodType = cart?.deliveryMethodType;

  const branchCode = cartOrderIdentifier?.branchCode ?? branchCodeFallback;

  const productResponse = useSWR(
    slug
      ? getProductKey({
          branchCode,
          slug,
          locale,
        })
      : false,
    async ([_, { branchCode, slug, locale }]) => {
      const product = await trpc.product.bySlug.query({
        locale,
        branchCode,
        slug,
      });

      return product;
    },
    {
      use: [laggy],
    },
  );

  const { data: favoriteProductCodes } = useFavoriteProductCodes();

  const { data, ...rest } = useMappedSWRResponse(
    productResponse,
    postProcessProductDetail(
      deliveryMethodType,
      favoriteProductCodes ? new Set(favoriteProductCodes) : null,
    ),
    [deliveryMethodType, favoriteProductCodes],
  );

  const dataRef = useRef(data);
  dataRef.current = data;

  const filteredConfiguration = useMemo(() => {
    if (!data?.configuration || isNone(configurationValues)) {
      return [];
    }

    return data.configuration
      .map((property) => {
        if (property.options.some((o) => Boolean(o.when?.length))) {
          return {
            ...property,
            options: property.options.filter((o) => {
              if (!o.when?.length) {
                return true;
              }

              return o.when.some((when) => {
                const referencedProperty = data.configuration.find(
                  (p) => p.id === when.property,
                );

                if (!referencedProperty) {
                  return false;
                }

                if (isSingleChoiceProperty(referencedProperty)) {
                  const value = selectSingleChoiceConfigurationPropertyValue(
                    configurationValues,
                    referencedProperty.id,
                  );

                  return value === when.value;
                } else {
                  const values = selectMultipleChoiceConfigurationPropertyValue(
                    configurationValues,
                    referencedProperty.id,
                  );

                  return values[when.value] ?? 0 > 0;
                }
              });
            }),
          };
        } else {
          return property;
        }
      })
      .filter((property) => property.options.length > 0);
  }, [data?.configuration, configurationValues]);

  return {
    ...rest,
    data,
    filteredConfiguration,
  };
}

/**
 * Use this to fetch the product detail.
 *
 * Also provides state of the product detail.
 *
 * @param slug The slug used to search for product in the displayed branch.
 * @param branchCodeFallback The branch code to use with SSR provided fallback.
 */
export function useProduct(
  slug: Maybe<string> | false,
  branchCodeFallback?: string,
  preferredVariant?: Maybe<string>,
  initialConfigurationValues?: ProductConfigurationPropertyValues,
) {
  const preferredVariantRef = useRef(preferredVariant);
  preferredVariantRef.current = preferredVariant;

  const { trpc, locale } = useApp();
  const [cartOrderIdentifier] = useCartOrderIdentifier();

  const { data: cart } = useCart();
  const deliveryMethodType = cart?.deliveryMethodType;

  const branchCode = cartOrderIdentifier?.branchCode ?? branchCodeFallback;

  const productResponse = useSWR(
    slug
      ? getProductKey({
          branchCode,
          slug,
          locale,
        })
      : false,
    async ([_, { branchCode, slug, locale }]) => {
      const product = await trpc.product.bySlug.query({
        locale,
        branchCode,
        slug,
      });

      return product;
    },
    {
      use: [laggy],
    },
  );

  const { data: favoriteProductCodes } = useFavoriteProductCodes();

  const { data, ...rest } = useMappedSWRResponse(
    productResponse,
    postProcessProductDetail(
      deliveryMethodType,
      favoriteProductCodes ? new Set(favoriteProductCodes) : null,
    ),
    [deliveryMethodType, favoriteProductCodes],
  );

  const dataRef = useRef(data);
  dataRef.current = data;

  const configurationValues = useProductDetailState(
    (state) => state.configurationValues,
  );

  const configurationValuesRef = useRef(configurationValues);
  configurationValuesRef.current = configurationValues;

  const {
    initialize,
    setSingleChoiceProperty,
    setMultipleChoiceProperty,
    setFreeformProperty,
  } = useProductDetailState(
    (state) => ({
      initialize: state.initialize,
      setSingleChoiceProperty: state.setSingleChoiceProperty,
      setMultipleChoiceProperty: state.setMultipleChoiceProperty,
      setFreeformProperty: state.setFreeformProperty,
    }),
    shallow,
  );

  const {
    data: configuratorProducts,
    isValidating: configuratorProductsValidating,
  } = useConfiguratorProducts(data?.configurationStyle === 'configurator');

  //
  // * ASSUMPTION: The product configuration is static.
  //
  useEffect(() => {
    if (data) {
      if (configuratorProductsValidating) {
        return;
      }

      let initialOrPreviousConfigurationValues = initialConfigurationValues;

      if (configurationValuesRef.current) {
        initialOrPreviousConfigurationValues = convertValuesForProduct(
          initialConfigurationValues ?? configurationValuesRef.current,
          configuratorProducts ?? [],
          data,
        );
      }

      initialize(
        data,
        preferredVariant,
        data.configurationStyle === 'configurator' ||
          data.configurationStyle === 'configurator_divided' ||
          data.configurationStyle === 'gift_card'
          ? initialOrPreviousConfigurationValues
          : {},
      );
    }
  }, [
    data?.id,
    configuratorProductsValidating,
    preferredVariant,
    initialConfigurationValues,
  ]);

  const clearConfiguration = useCallback(() => {
    if (dataRef.current) {
      initialize(dataRef.current, preferredVariantRef.current, null);
    }
  }, [initialize]);

  const filteredConfiguration = useMemo(() => {
    if (!data?.configuration) {
      return [];
    }

    return data.configuration
      .map((property) => {
        if (property.options.some((o) => Boolean(o.when?.length))) {
          return {
            ...property,
            options: property.options.filter((o) => {
              if (!o.when?.length) {
                return true;
              }

              return o.when.some((when) => {
                const referencedProperty = data.configuration.find(
                  (p) => p.id === when.property,
                );

                if (!referencedProperty) {
                  return false;
                }

                if (isSingleChoiceProperty(referencedProperty)) {
                  const value = selectSingleChoiceConfigurationPropertyValue(
                    configurationValues,
                    referencedProperty.id,
                  );

                  return value === when.value;
                } else {
                  const values = selectMultipleChoiceConfigurationPropertyValue(
                    configurationValues,
                    referencedProperty.id,
                  );

                  return values[when.value] ?? 0 > 0;
                }
              });
            }),
          };
        } else {
          return property;
        }
      })
      .filter((property) => property.options.length > 0);
  }, [data?.configuration, configurationValues]);

  const selectedOptionCodes = useMemo(() => {
    return Object.entries(configurationValues)
      .map(([configId, selectedItemCode]) => {
        if (!selectedItemCode) return null;

        const property = filteredConfiguration.find(
          (prop) => prop.id === configId,
        );
        if (!property) return null;

        const selectedOption = property.options.find(
          (option) => option.itemCode === selectedItemCode,
        );

        return selectedOption ? selectedOption.itemCode : null;
      })
      .filter((code): code is string => typeof code === 'string');
  }, [configurationValues, filteredConfiguration]);

  const fetchNutritionValues = async (optionIds: string[]) => {
    const nutritionPromises = optionIds.map((productCode) =>
      trpc.product.productItem.query({ productCode }),
    );

    return Promise.all(nutritionPromises);
  };

  const { data: optionsNutritionValues } = useSWR(
    selectedOptionCodes.length
      ? ['productNutrition', ...selectedOptionCodes]
      : null,
    () => fetchNutritionValues(selectedOptionCodes),
  );

  return {
    ...rest,
    data,
    configurationValues,
    filteredConfiguration,
    setSingleChoiceProperty,
    setMultipleChoiceProperty,
    setFreeformProperty,
    clearConfiguration,
    optionsNutritionValues,
  };
}

export const useClearProductConfiguration = () =>
  useProductDetailState((state) => state.clearConfiguration);

export async function fetchProduct(
  app: App,
  { branchCode, slug }: { readonly branchCode: string; readonly slug: string },
) {
  const data = await app.trpc.product.bySlug.query({
    locale: app.locale,
    branchCode,
    slug,
  });

  return data;
}

export function asProductFallback(
  app: App,
  { branchCode, slug }: { readonly branchCode: string; readonly slug: string },
  data: Awaited<ReturnType<typeof fetchProduct>>,
) {
  return {
    [unstable_serialize(
      getProductKey({ branchCode, slug, locale: app.locale }),
    )]: data,
  };
}

/**
 * Fetch the product detail fallback.
 */
export async function fetchProductFallback(
  app: App,
  { branchCode, slug }: { readonly branchCode: string; readonly slug: string },
) {
  const data = await fetchProduct(app, { branchCode, slug });

  return asProductFallback(app, { branchCode, slug }, data);
}

function getProductTaxonKey({
  locale,
  branchCode,
  tag,
}: {
  readonly locale: string;
  readonly branchCode?: Maybe<string>;
  readonly tag?: Maybe<string>;
}) {
  return ['product.taxons', { locale, branchCode, tag }] as const;
}

export function useProductTaxonsLoading() {
  return useCellValue(PRODUCT_TAXONS_LOADING);
}
const PRODUCT_TAXONS_LOADING = new Cell<boolean>(true);

export function useProductTaxons(
  branchCode?: Maybe<string> | false,
  tag?: Maybe<string>,
) {
  const { trpc, locale } = useApp();

  const response = useSWR(
    branchCode !== false
      ? getProductTaxonKey({ locale, branchCode, tag })
      : false,
    async ([_, { locale, branchCode }]) => {
      const taxons = await trpc.product.taxons.query({
        locale,
        branchCode,
        tag,
      });

      return taxons;
    },
    {
      use: [laggy],
    },
  );

  useEffect(() => {
    if (response.isLoading !== PRODUCT_TAXONS_LOADING.value) {
      PRODUCT_TAXONS_LOADING.value = response.isLoading;
    }
  }, [response.isLoading]);

  return response;
}

export async function fetchProductTaxons(app: App, branchCode?: Maybe<string>) {
  const data = await app.trpc.product.taxons.query({
    locale: app.locale,
    branchCode,
  });

  return data;
}

export async function fetchProductTaxonsFallback(
  app: App,
  branchCode?: Maybe<string>,
) {
  const data = await fetchProductTaxons(app, branchCode);

  return {
    [unstable_serialize(
      getProductTaxonKey({ locale: app.locale, branchCode }),
    )]: data,
  };
}

export function useFavoriteProducts() {
  const { data: cart } = useCart();
  const allProductsResponse = useProductList({ branchCode: cart?.branchCode });

  return {
    ...allProductsResponse,
    data: useMemo(() => {
      return allProductsResponse.data
        ?.flatMap?.((taxon) => taxon.products)
        ?.filter?.((product) => product.favorite);
    }, [allProductsResponse.data]),
  };
}

export function useConfiguratorProducts(load = true) {
  const { trpc, locale } = useApp();
  const { data: cart } = useCart();

  const deliveryMethodType = cart?.deliveryMethodType;

  const configuratorProductsResponse = useSWR(
    load
      ? [
          'product.allConfiguratorProducts',
          { locale, branchCode: cart?.branchCode },
        ]
      : false,
    async ([_, { locale, branchCode }]) => {
      const products = await trpc.product.allConfiguratorProducts.query({
        locale,
        branchCode,
      });

      return products;
    },
    {
      use: [laggy],
    },
  );

  return useMappedSWRResponse(
    configuratorProductsResponse,
    postProcessConfiguratorProducts(deliveryMethodType),
    [deliveryMethodType],
  );
}

export function useGiftCardProducts(load = true) {
  const { trpc, locale } = useApp();
  const { data: cart } = useCart();

  const deliveryMethodType = cart?.deliveryMethodType;

  const configuratorProductsResponse = useSWR(
    load
      ? ['product.allGiftCards', { locale, branchCode: cart?.branchCode }]
      : false,
    async ([_, { locale, branchCode }]) => {
      const products = await trpc.product.allGiftCards.query({
        locale,
        branchCode,
      });

      return products;
    },
    {
      use: [laggy],
    },
  );

  return useMappedSWRResponse(
    configuratorProductsResponse,
    postProcessGiftCardProducts(deliveryMethodType),
    [deliveryMethodType],
  );
}

export async function fetchConfiguratorProductsFallback(app: App) {
  const data = await app.trpc.product.allConfiguratorProducts.query({
    locale: app.locale,
  });

  return {
    [unstable_serialize([
      'product.allConfiguratorProducts',
      { locale: app.locale },
    ])]: data,
  };
}

export function isAdjustmentToPrice(
  role: string,
): role is
  | 'pastry'
  | 'dough'
  | 'custom_menu_pastry'
  | 'menu_incentive'
  | 'product_menu'
  | 'product_menu_item' {
  return [
    'pastry',
    'dough',
    'custom_menu_pastry',
    'menu_incentive',
    'product_menu',
    'product_menu_item',
  ].includes(role);
}

function getFavoriteConfiguratorProductsKey({
  locale,
  branchCode,
}: {
  readonly locale: string;
  readonly branchCode?: Maybe<string>;
}) {
  return [
    'product.favoriteConfiguratorProducts',
    { locale, branchCode },
  ] as const;
}

export function useFavoriteProductConfigurations(load = true) {
  const {
    data: products,
    isValidating: isValidatingProducts,
    error: errorProducts,
  } = useConfiguratorProducts(load);

  const { trpc, locale } = useApp();
  const { data: cart } = useCart();
  const { data: customer } = useAuthorizedCustomer();

  const {
    data: savedConfigurations,
    isValidating: isValidatingSavedConfigurations,
    error: errorSavedConfigurations,
    mutate,
  } = useSWR(
    load && isSome(customer)
      ? getFavoriteConfiguratorProductsKey({
          locale,
          branchCode: cart?.branchCode,
        })
      : false,
    async ([_, { locale, branchCode }]) => {
      const configurations =
        await trpc.product.favoriteConfiguratorProducts.query({
          locale,
          branchCode,
        });

      return configurations;
    },
    {
      revalidateOnMount: true,
    },
  );

  const configurations = useMemo(() => {
    if (!products || !savedConfigurations) {
      return;
    }

    return savedConfigurations.flatMap((configuration) => {
      const product = products.find(
        (p) => p.code === configuration.configuratorProduct,
      );

      if (!product) {
        return [];
      }

      return {
        configurationId: configuration.id,
        configurationName: configuration.name,
        configurationValues: configuration.configuration,
        ingredients: configuration.ingredients,
        imagePaths: configuration.imagePaths,
        ...product,
      };
    });
  }, [products, savedConfigurations]);

  const saveConfiguration = useCallback(
    async (
      configuratorProduct: string,
      name: string,
      configuration: Record<
        string,
        Record<string, number | undefined> | string | null | undefined
      >,
    ) => {
      await mutate(
        async (data) => {
          await trpc.product.createFavoriteConfigurator.mutate({
            name,
            configuratorProduct,
            configuration: configuration as Record<
              string,
              Record<string, number> | string | null
            >,
          });

          return data;
        },
        {
          revalidate: true,
        },
      );
    },
    [],
  );

  const removeConfiguration = useCallback(
    async (configurationId: number) => {
      await mutate(
        trpc.product.removeFavoriteConfigurator.mutate({
          id: configurationId,
        }),
        {
          revalidate: true,
          optimisticData: (currentData) =>
            currentData?.filter?.(
              (configuration) => configuration.id !== configurationId,
            ) ?? [],
        },
      );
    },
    [trpc, mutate],
  );

  return {
    saveConfiguration,
    removeConfiguration,
    data: configurations,
    isValidating: isValidatingProducts || isValidatingSavedConfigurations,
    error: errorProducts ?? errorSavedConfigurations,
  };
}

export type SavedConfiguratorProducts = ReturnType<
  typeof useFavoriteProductConfigurations
>['data'];

export function useConfiguratorProductList(tagFilter: readonly string[]) {
  const { locale } = useApp();

  const FAVORITE_TAG_ID = 'favorite';
  const configuratorProductsResponse = useConfiguratorProducts();
  const savedConfiguratorProductsResponse = useFavoriteProductConfigurations();

  const firstConfiguratorProduct = configuratorProductsResponse.data?.[0];
  const lastTwoSavedConfiguratorProducts =
    savedConfiguratorProductsResponse.data?.slice(-2) ?? [];

  const products = [];

  if (firstConfiguratorProduct) {
    const defaultVariant = firstConfiguratorProduct.variants.find(
      (v) => v.default,
    );

    products.push({
      favorite: false,
      id: firstConfiguratorProduct.id,
      variants: defaultVariant ? [defaultVariant] : [],
      code: firstConfiguratorProduct.code,
      image: firstConfiguratorProduct.image ?? null,
      title: locale === 'en' ? 'Make your own' : 'Sestav si sám',
      shortDescription: firstConfiguratorProduct.shortDescription,
      slug: firstConfiguratorProduct.slug,
      tags: [],
      available: firstConfiguratorProduct.available,
      allergens: firstConfiguratorProduct.allergens,
      configurationStyle: firstConfiguratorProduct.configurationStyle,
      conditionallyAvailable: null,
    });
  }

  if (tagFilter.length === 0 || tagFilter.includes(FAVORITE_TAG_ID)) {
    for (const configuration of lastTwoSavedConfiguratorProducts) {
      const defaultVariant = configuration.configuration
        .find((p) => p.role === 'variant')
        ?.options?.find?.((p) => p.default);

      products.push({
        favorite: true,
        id: configuration.configurationId,
        variants: defaultVariant
          ? [
              {
                price: null,
                default: true,
                name: defaultVariant.name,
                code: defaultVariant.itemCode,
                shortName: defaultVariant.shortName,
                size: null,
              },
            ]
          : [],
        code: configuration.code,
        image: configuration.imagePaths ?? configuration.image ?? null,
        title: configuration.configurationName ?? configuration.title,
        description: configuration.ingredients,
        shortDescription:
          configuration.ingredients ?? configuration.shortDescription,
        slug: configuration.slug,
        tags: [],
        available: configuration.available,
        allergens: configuration.allergens,
        configurationStyle: configuration.configurationStyle,
        configuration: configuration.configurationValues,
        conditionallyAvailable: null,
      });
    }
  }

  return {
    data: {
      id: -1,
      name: locale === 'en' ? 'MY RECIPES' : 'MOJE RECEPTURY',
      image: null,
      slug: locale === 'en' ? 'my-recipes' : 'moje-receptury',
      products,
    },
    isValidating:
      configuratorProductsResponse.isValidating ||
      savedConfiguratorProductsResponse.isValidating,
    error:
      configuratorProductsResponse.error ??
      savedConfiguratorProductsResponse.error,
  };
}

export type ConfiguratorProductList = ReturnType<
  typeof useConfiguratorProductList
>['data'];

export function isUnavailableForDelivery(
  product: Pick<ProductDetail, 'conditionallyAvailable' | 'available'>,
  cart: Maybe<RouterOutputs['order']['byTokenValue']>,
) {
  return Boolean(
    (!cart || cart.deliveryMethodType == 'courier') &&
      product.conditionallyAvailable?.includes?.('unavailable_for_delivery'),
  );
}

export function isUnavailableForPickup(
  product: Pick<ProductDetail, 'conditionallyAvailable' | 'available'>,
  cart: Maybe<RouterOutputs['order']['byTokenValue']>,
) {
  return Boolean(
    (!cart || cart.deliveryMethodType === 'atBranch') &&
      product.conditionallyAvailable?.includes?.('unavailable_for_pickup'),
  );
}

export function isConditionallyAvailable(
  product: Pick<ProductDetail, 'conditionallyAvailable' | 'available'>,
  cart: Maybe<RouterOutputs['order']['byTokenValue']>,
) {
  const unavailableForDelivery = isUnavailableForDelivery(product, cart);
  const unavailableForPickup = isUnavailableForPickup(product, cart);

  return product.available && !unavailableForDelivery && !unavailableForPickup;
}

export function isMenuUnavailable(
  product: v2_ProductDetail,
  filteredConfiguration: v2_ProductDetail['configuration'],
): boolean {
  return (
    product.configurationStyle === 'custom_menu' &&
    product.configuration.some((i) => i.options.some((o) => !o.available)) &&
    filteredConfiguration.every((p) => p.hidden)
  );
}

function useAdditionalSalesProductCodes() {
  const { data: cart } = useCart();
  const { trpc } = useApp();

  return useSWR(
    cart ? ['brach.additionalSales', { branchCode: cart.branchCode }] : false,
    async ([_, { branchCode }]) => {
      const codes = await trpc.branch.additionalSales.query({ branchCode });

      return codes;
    },
    {},
  );
}

export function useAdditionalSalesProducts() {
  const { data: productList, ...response } = useProductList();
  const { data: additionalSalesProductCodes } =
    useAdditionalSalesProductCodes();

  const data = useMemo(() => {
    if (productList == null || additionalSalesProductCodes == null) {
      return null;
    }

    const productsWithTaxons = productList.flatMap(({ products, ...taxon }) =>
      products.map((p) => ({ ...p, taxon })),
    );

    return additionalSalesProductCodes.flatMap((code) => {
      const p = productsWithTaxons.find((product) => product.code === code);

      if (p == null) {
        return [];
      }

      return [p];
    });
  }, [productList, additionalSalesProductCodes]);

  return {
    data,
    ...response,
  };
}

export function getProductTitles(
  products: NonNullable<ReturnType<typeof useProductList>['data']>,
  productCodes: string[],
): string[] {
  return productCodes
    .flatMap((code) => {
      const product = products
        .flatMap((p) => p.products)
        .find((p) => p.code === code);

      return product != null ? [product] : [];
    })
    .map((p) => p?.title);
}
